import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Mail, Phone, MapPin } from "lucide-react";
import { CONTACT, IMAGES, NAV_LINKS, LEGAL_LINKS } from "@/content/siteContent";
import { CursorHalo } from "@/components/CursorHalo";
import { CookieConsent } from "@/components/CookieConsent";

/* Sets per-page title + meta description */
export const PageMeta = ({ title, description }) => {
    useEffect(() => {
        document.title = title;
        const tag = document.querySelector('meta[name="description"]');
        if (tag) tag.setAttribute("content", description);
    }, [title, description]);
    return null;
};

export const ScrollToTop = () => {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [pathname]);
    return null;
};

const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [pathname]);

    return (
        <header
            className={`site-header fixed inset-x-0 top-0 z-50 ${scrolled || open ? "is-scrolled" : ""}`}
            data-testid="site-header"
        >
            <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
                <Link
                    to="/"
                    className="flex items-center gap-3"
                    data-testid="header-logo-link"
                    aria-label="Dasamahavidya Puja — home"
                >
                    <img
                        src={IMAGES.logoTransparent}
                        alt="Chetla Dasamahavidya Puja logo"
                        className="h-11 w-11 rounded-full object-contain"
                        width="44"
                        height="44"
                    />
                    <span className="leading-tight">
                        <span className="font-display block text-lg font-semibold tracking-wide text-[#FFF5E6]">
                            Dasamahavidya Puja
                        </span>
                        <span className="block text-[0.6rem] uppercase tracking-[0.3em] text-[#C8A15A]">
                            Chetla · Kolkata
                        </span>
                    </span>
                </Link>

                <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary" data-testid="desktop-nav">
                    {NAV_LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            className={({ isActive }) => `nav-link ${isActive ? "is-active" : ""}`}
                            data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <button
                    type="button"
                    className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#C8A15A]/40 text-[#FFF5E6] lg:hidden"
                    onClick={() => setOpen((v) => !v)}
                    aria-expanded={open}
                    aria-label={open ? "Close navigation menu" : "Open navigation menu"}
                    data-testid="mobile-menu-toggle"
                >
                    {open ? <X size={20} /> : <Menu size={20} />}
                </button>
            </div>

            {open && (
                <nav
                    className="border-t border-[#C8A15A]/15 bg-[#10080B]/97 px-6 pb-8 pt-4 backdrop-blur-md lg:hidden"
                    aria-label="Mobile"
                    data-testid="mobile-nav"
                >
                    <ul className="flex flex-col gap-1">
                        {NAV_LINKS.map((l) => (
                            <li key={l.to}>
                                <NavLink
                                    to={l.to}
                                    end={l.to === "/"}
                                    className={({ isActive }) =>
                                        `block py-3 text-sm uppercase tracking-[0.18em] transition-colors ${
                                            isActive ? "text-[#C8A15A]" : "text-[#FFF5E6]/80"
                                        }`
                                    }
                                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                >
                                    {l.label}
                                </NavLink>
                            </li>
                        ))}
                    </ul>
                </nav>
            )}
        </header>
    );
};

const Footer = () => {
    return (
        <footer className="border-t border-[#C8A15A]/15 bg-[#0c0508]" data-testid="site-footer">
            <div className="mx-auto grid max-w-7xl gap-12 px-5 py-16 sm:px-8 md:grid-cols-2 lg:grid-cols-4">
                <div>
                    <div className="flex items-center gap-3">
                        <img
                            src={IMAGES.logoTransparent}
                            alt="Chetla Dasamahavidya Puja logo"
                            className="h-12 w-12 rounded-full object-contain"
                            width="48"
                            height="48"
                            loading="lazy"
                        />
                        <span className="font-display text-xl font-semibold text-[#FFF5E6]">
                            Dasamahavidya Puja
                        </span>
                    </div>
                    <p className="mt-5 max-w-xs text-sm leading-relaxed text-[#FFF5E6]/60">
                        A living tradition of devotion, culture and community, organised by Chetla
                        Sarbasadharaner Kali Puja Trust.
                    </p>
                </div>

                <nav aria-label="Footer — explore">
                    <h3 className="eyebrow">Explore</h3>
                    <ul className="mt-5 space-y-3">
                        {NAV_LINKS.map((l) => (
                            <li key={l.to}>
                                <Link
                                    to={l.to}
                                    className="text-sm text-[#FFF5E6]/70 transition-colors hover:text-[#C8A15A]"
                                    data-testid={`footer-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                >
                                    {l.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                <nav aria-label="Footer — legal">
                    <h3 className="eyebrow">Legal</h3>
                    <ul className="mt-5 space-y-3">
                        {LEGAL_LINKS.map((l) => (
                            <li key={l.to}>
                                <Link
                                    to={l.to}
                                    className="text-sm text-[#FFF5E6]/70 transition-colors hover:text-[#C8A15A]"
                                    data-testid={`footer-legal-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                >
                                    {l.label}
                                </Link>
                            </li>
                        ))}
                        <li>
                            <button
                                type="button"
                                onClick={() => window.dispatchEvent(new Event("cdp:open-cookie-settings"))}
                                className="text-sm text-[#FFF5E6]/70 transition-colors hover:text-[#C8A15A]"
                                data-testid="footer-cookie-settings-button"
                            >
                                Cookie Settings
                            </button>
                        </li>
                    </ul>
                </nav>

                <div>
                    <h3 className="eyebrow">Contact</h3>
                    <ul className="mt-5 space-y-4 text-sm text-[#FFF5E6]/70">
                        <li>
                            <a
                                href={`mailto:${CONTACT.email}`}
                                className="inline-flex items-center gap-2.5 transition-colors hover:text-[#C8A15A]"
                                data-testid="footer-email-link"
                            >
                                <Mail size={15} className="shrink-0 text-[#C8A15A]" />
                                {CONTACT.email}
                            </a>
                        </li>
                        <li>
                            <a
                                href={CONTACT.phoneHref}
                                className="inline-flex items-center gap-2.5 transition-colors hover:text-[#C8A15A]"
                                data-testid="footer-phone-link"
                            >
                                <Phone size={15} className="shrink-0 text-[#C8A15A]" />
                                {CONTACT.phoneDisplay}
                            </a>
                        </li>
                        <li className="flex items-start gap-2.5">
                            <MapPin size={15} className="mt-0.5 shrink-0 text-[#C8A15A]" />
                            <span data-testid="footer-address">
                                {CONTACT.address}
                                <br />
                                {CONTACT.locality}
                            </span>
                        </li>
                    </ul>
                </div>
            </div>

            <div className="border-t border-[#C8A15A]/10">
                <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-5 py-6 text-[0.72rem] uppercase tracking-[0.18em] text-[#FFF5E6]/40 sm:flex-row sm:px-8">
                    <p data-testid="footer-copyright">
                        © {new Date().getFullYear()} Chetla Sarbasadharaner Kali Puja Trust
                    </p>
                    <p>Chetla · Kolkata · 112 years of Shyama Maa</p>
                </div>
            </div>
        </footer>
    );
};

export const Layout = ({ children }) => {
    return (
        <div className="flex min-h-screen flex-col bg-[#10080B]">
            <CursorHalo />
            <Header />
            <main className="flex-1">{children}</main>
            <Footer />
            <CookieConsent />
        </div>
    );
};
